// Observes when elements enter the viewport for lazy loading

import { useEffect, useRef, useState, RefObject } from 'react';

interface UseSystemViewportObserverOptions {
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
}

export const useSystemViewportObserver = (
  options: UseSystemViewportObserverOptions = {}
): [RefObject<HTMLDivElement | null>, boolean] => {
  const { threshold = 0, rootMargin = '200px', triggerOnce = true } = options;
  const ref = useRef<HTMLDivElement>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const element = ref.current;
    if (!element || (triggerOnce && isIntersecting)) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          if (triggerOnce) {
            observer.disconnect();
          }
        } else if (!triggerOnce) {
          setIsIntersecting(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold, rootMargin, triggerOnce, isIntersecting]);

  return [ref, isIntersecting];
};
